import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';

export interface BladeErrorFallbackProps {
  /**
   * The error that was caught
   */
  error: Error;

  /**
   * Error info from React error boundary
   */
  errorInfo: React.ErrorInfo;

  /**
   * Function to reset the error boundary and retry
   */
  reset: () => void;

  /**
   * Optional custom title for the error message
   */
  title?: string;

  /**
   * Optional custom description for the error
   */
  description?: string;

  /**
   * URL to navigate to when closing the error modal
   * @default Current parent route (removes last segment)
   */
  closeUrl?: string;

  /**
   * Whether to show error details in development mode
   * @default true
   */
  showDetails?: boolean;

  /**
   * Custom action to perform on close instead of navigation
   */
  onClose?: () => void;
}

/**
 * Reusable error fallback component for blade/drawer components
 *
 * Displays a full-screen modal overlay with error details and recovery options.
 * Designed to be used with React Error Boundaries for catching errors in
 * drawer/blade components throughout the application.
 *
 * @example
 * ```tsx
 * <ErrorBoundary
 *   boundaryName="TaskDetails"
 *   fallback={(error, errorInfo, reset) => (
 *     <BladeErrorFallback
 *       error={error}
 *       errorInfo={errorInfo}
 *       reset={reset}
 *       closeUrl="/tasks"
 *     />
 *   )}
 * >
 *   <TaskDetailsPage />
 * </ErrorBoundary>
 * ```
 */
export const BladeErrorFallback: React.FC<BladeErrorFallbackProps> = ({
  error,
  errorInfo,
  reset,
  title = 'Unable to Load Details',
  description = 'This item encountered an error and could not be loaded. The data may be invalid.',
  closeUrl,
  showDetails = true,
  onClose,
}) => {
  const navigate = useNavigate();

  const handleClose = () => {
    reset();

    if (onClose) {
      onClose();
    } else if (closeUrl) {
      navigate(closeUrl);
    } else {
      // Default: navigate to parent route
      const currentPath = window.location.pathname;
      const parentPath =
        currentPath.substring(0, currentPath.lastIndexOf('/')) || '/';
      navigate(parentPath);
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          backgroundColor: 'white',
          padding: 4,
          borderRadius: 2,
          maxWidth: '600px',
          textAlign: 'center',
          boxShadow: 24,
        }}
      >
        <Typography variant="h4" color="error" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body1" paragraph>
          {description}
        </Typography>
        {showDetails && process.env['NODE_ENV'] === 'development' && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
              textAlign: 'left',
              maxHeight: '200px',
              overflow: 'auto',
            }}
          >
            <Typography
              variant="caption"
              component="div"
              sx={{ fontWeight: 'bold', mb: 1 }}
            >
              Error Details (Development Only):
            </Typography>
            <Typography
              variant="caption"
              component="pre"
              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {error.message}
              {'\n\n'}
              {error.stack}
            </Typography>
            {errorInfo?.componentStack && (
              <>
                <Typography
                  variant="caption"
                  component="div"
                  sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}
                >
                  Component Stack:
                </Typography>
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {errorInfo.componentStack}
                </Typography>
              </>
            )}
          </Box>
        )}
        <Box
          sx={{
            mt: 3,
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
          }}
        >
          <Button variant="contained" onClick={reset}>
            Try Again
          </Button>
          <Button variant="outlined" onClick={handleClose}>
            Close
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default BladeErrorFallback;
